/**
 * Meeting summary templates API (built-in list + org custom templates +
 * per-recurring-event defaults)
 */

import type {
  CreateMeetingSummaryTemplateType,
  MeetingSummaryTemplateDef,
} from '@scrambl/shared'

import { apiClient } from './api-client'

export interface CustomSummaryTemplate {
  id: string
  organizationId: string
  createdBy: string | null
  name: string
  instructions: string
  createdAt: string
  updatedAt: string
}

export interface SummaryTemplateList {
  builtin: MeetingSummaryTemplateDef[]
  custom: CustomSummaryTemplate[]
}

export interface TemplatePreference {
  eventKey: string
  templateRef: string
}

const BASE = '/meeting-summary-templates'

class MeetingSummaryTemplatesApiService {
  private static instance: MeetingSummaryTemplatesApiService | null = null
  static getInstance() {
    return (this.instance ??= new MeetingSummaryTemplatesApiService())
  }

  async list(): Promise<SummaryTemplateList> {
    return apiClient<SummaryTemplateList>(BASE)
  }

  async create(
    body: CreateMeetingSummaryTemplateType,
  ): Promise<CustomSummaryTemplate> {
    return apiClient<CustomSummaryTemplate>(BASE, { method: 'POST', body })
  }

  async update(
    id: string,
    body: Partial<CreateMeetingSummaryTemplateType>,
  ): Promise<CustomSummaryTemplate> {
    return apiClient<CustomSummaryTemplate>(`${BASE}/${id}`, {
      method: 'PATCH',
      body,
    })
  }

  async remove(id: string): Promise<void> {
    await apiClient(`${BASE}/${id}`, { method: 'DELETE' })
  }

  async listPreferences(): Promise<TemplatePreference[]> {
    return apiClient<TemplatePreference[]>(`${BASE}/preferences`)
  }

  /** templateRef null clears the event's default */
  async setPreference(
    eventKey: string,
    templateRef: string | null,
  ): Promise<void> {
    await apiClient(`${BASE}/preferences`, {
      method: 'PUT',
      body: { eventKey, templateRef },
    })
  }
}

export const meetingSummaryTemplatesApi =
  MeetingSummaryTemplatesApiService.getInstance()
